import { useState, useCallback } from 'react';
import { Box, Trash2, RefreshCw, FileText, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '@/hooks/use-api';
import { Header } from '@/components/layout/header';
import { LoadingState } from '@/components/loading-state';
import { ErrorState } from '@/components/error-state';
import { MetricCard } from '@/components/metric-card';
import { CpuCell } from '@/components/cpu-cell';
import { StatusBadge } from '@/components/status-badge';
import { ec2InstanceStatus } from '@/lib/status';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatRelativeTime } from '@/lib/format';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Devbox {
  devboxId: string;
  instanceId: string;
  state: string;
  instanceType: string;
  launchTime: string;
  publicIp: string | null;
  backendUrl: string;
  frontendBranch: string | null;
  tags: Record<string, string>;
  cpu: number | null;
}

interface LogEntry {
  timestamp: string;
  message: string;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DevboxesPage() {
  const { data, error, loading, refresh, lastFetched } = useApi<{ devboxes: Devbox[] }>(
    '/api/devboxes'
  );

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [logDialog, setLogDialog] = useState<{
    id: string;
    logs: LogEntry[];
  } | null>(null);
  const [logLoading, setLogLoading] = useState(false);

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------

  const handleTeardown = useCallback(
    async (devboxId: string) => {
      if (!window.confirm(`Tear down devbox "${devboxId}"? This cannot be undone.`)) {
        return;
      }
      setActionLoading(devboxId);
      try {
        const res = await fetch(`/api/devboxes/${devboxId}/teardown`, {
          method: 'POST',
        });
        if (!res.ok) {
          const body = await res.text();
          throw new Error(body || `${res.status}`);
        }
        toast.success(`Devbox "${devboxId}" teardown initiated`);
        refresh();
      } catch (err) {
        toast.error(`Teardown failed: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setActionLoading(null);
      }
    },
    [refresh]
  );

  const handleRebuild = useCallback(
    async (devboxId: string) => {
      if (!window.confirm(`Rebuild devbox "${devboxId}"?`)) {
        return;
      }
      setActionLoading(devboxId);
      try {
        const res = await fetch(`/api/devboxes/${devboxId}/rebuild`, {
          method: 'POST',
        });
        if (!res.ok) {
          const body = await res.text();
          throw new Error(body || `${res.status}`);
        }
        toast.success(`Devbox "${devboxId}" rebuild initiated`);
        refresh();
      } catch (err) {
        toast.error(`Rebuild failed: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setActionLoading(null);
      }
    },
    [refresh]
  );

  const handleViewLogs = useCallback(async (devboxId: string) => {
    setLogLoading(true);
    try {
      const res = await fetch(`/api/devboxes/${devboxId}/logs`);
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `${res.status}`);
      }
      const json = (await res.json()) as { logs: LogEntry[] };
      setLogDialog({ id: devboxId, logs: json.logs });
    } catch (err) {
      toast.error(`Failed to fetch logs: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLogLoading(false);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const devboxes = data?.devboxes ?? [];

  return (
    <div className="flex flex-col">
      <Header
        title="Devbox Environments"
        lastFetched={lastFetched}
        onRefresh={refresh}
        loading={loading}
      />

      <div className="space-y-6 p-6">
        {/* Error */}
        {error && <ErrorState message={error} onRetry={refresh} />}

        {/* Loading */}
        {loading && !data && <LoadingState rows={3} />}

        {/* Metric */}
        {data && (
          <div className="max-w-xs">
            <MetricCard
              label="Active Devboxes"
              value={devboxes.filter((d) => d.state === 'running').length}
              icon={<Box className="h-4 w-4" />}
              metricKey="devbox.count"
            />
          </div>
        )}

        {/* Empty state */}
        {data && devboxes.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Box className="text-muted-foreground mx-auto mb-4 h-10 w-10" />
              <p className="text-muted-foreground">No devboxes running — nothing to worry about.</p>
            </CardContent>
          </Card>
        )}

        {/* Devbox table */}
        {devboxes.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Box className="h-5 w-5" />
                Devboxes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TooltipProvider>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Devbox ID</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>CPU</TableHead>
                      <TableHead>Uptime</TableHead>
                      <TableHead>Backend URL</TableHead>
                      <TableHead>Frontend Branch</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {devboxes.map((db) => (
                      <TableRow key={db.devboxId}>
                        <TableCell className="font-mono text-xs">{db.devboxId}</TableCell>
                        <TableCell>
                          <StatusBadge status={ec2InstanceStatus(db.state)} label={db.state} />
                        </TableCell>
                        <TableCell>
                          <CpuCell cpu={db.cpu} />
                        </TableCell>
                        <TableCell>{formatRelativeTime(new Date(db.launchTime))}</TableCell>
                        <TableCell>
                          <a
                            href={db.backendUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 underline-offset-2 hover:underline"
                            title={db.backendUrl}
                          >
                            {db.backendUrl
                              .replace('https://', '')
                              .replace('.dev.getoverhead.com', '')}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {db.frontendBranch ?? '—'}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{db.publicIp ?? '—'}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-xs"
                                  disabled={actionLoading === db.devboxId || logLoading}
                                  onClick={() => handleViewLogs(db.devboxId)}
                                >
                                  <FileText className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View Logs</TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-xs"
                                  disabled={actionLoading === db.devboxId}
                                  onClick={() => handleRebuild(db.devboxId)}
                                >
                                  <RefreshCw className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Rebuild</TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-xs"
                                  className="text-destructive hover:text-destructive"
                                  disabled={actionLoading === db.devboxId}
                                  onClick={() => handleTeardown(db.devboxId)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Teardown</TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TooltipProvider>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Log viewer dialog */}
      <Dialog
        open={logDialog !== null}
        onOpenChange={(open) => {
          if (!open) setLogDialog(null);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Logs — {logDialog?.id}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[400px]">
            <pre className="p-4 font-mono text-xs leading-relaxed">
              {logDialog?.logs.length === 0 && (
                <span className="text-muted-foreground">No log entries.</span>
              )}
              {logDialog?.logs.map((entry, i) => (
                <div key={i}>
                  <span className="text-muted-foreground">[{entry.timestamp}]</span> {entry.message}
                </div>
              ))}
            </pre>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
